import { jsx } from "react/jsx-runtime";
import React__default, { useState } from "react";
import classNames from "classnames";
import { FormOnColor, FormSize, AnalyticsId } from "./constants.js";
import { usePseudoClasses } from "./hooks/usePseudoClasses.js";
import { useUuid } from "./hooks/useUuid.js";
import { getAriaDescribedBy } from "./utils/accessibility.js";
import { isMutableRefObject, mergeRefs } from "./utils/refs.js";
import { u as uuid } from "./uuid.js";
import { E as ErrorWrapper } from "./ErrorWrapper.js";
import { g as getLabelText, r as renderLabelAsParent } from "./Label.js";
import radioButtonStyles from "./components/RadioButton/styles.module.scss";
const getRadioLabelClasses = (radioId, onColor, large, checkedRadioId) => {
  const onCherry = onColor === "oninvalid";
  const checked = radioId === checkedRadioId;
  return classNames({
    [radioButtonStyles["radio-button-label__large--on-grey"]]: large && onColor === "ongrey" && !checked,
    [radioButtonStyles["radio-button-label__large--on-blueberry"]]: onColor === "onblueberry" && !checked && large,
    [radioButtonStyles["radio-button-label__large--selected"]]: large && checked && !onCherry,
    [radioButtonStyles["radio-button-label__large--selected-invalid"]]: large && checked && onCherry
  });
};
const RadioButton = React__default.forwardRef((props, ref) => {
  const {
    className,
    defaultChecked,
    onChange,
    disabled,
    label,
    inputId = uuid(),
    onColor = FormOnColor.onwhite,
    name = inputId,
    size,
    errorText,
    error = !!errorText,
    errorTextId,
    errorWrapperClassName,
    value = getLabelText(label),
    testId,
    required,
    labelClassNames,
    ...rest
  } = props;
  const invalid = error || onColor === FormOnColor.oninvalid;
  const onDark = onColor === FormOnColor.ondark;
  const onBlueberry = onColor === FormOnColor.onblueberry;
  const onCherry = onColor === FormOnColor.oninvalid;
  const isLarge = size === FormSize.large;
  const [checked, changeChecked] = useState();
  const { refObject, isFocused } = usePseudoClasses(isMutableRefObject(ref) ? ref : null);
  const mergedRefs = mergeRefs([ref, refObject]);
  const errorTextUuid = useUuid(errorTextId);
  const radioButtonWrapperClasses = classNames(radioButtonStyles["radio-button-wrapper"], {
    [radioButtonStyles["radio-button-wrapper__large"]]: isLarge,
    [radioButtonStyles["radio-button-wrapper__large--focused"]]: isLarge && isFocused,
    [radioButtonStyles["radio-button-wrapper__large--selected"]]: isLarge && checked && isFocused,
    [radioButtonStyles["radio-button-wrapper__large--invalid"]]: isLarge && onCherry && isFocused,
    [radioButtonStyles["radio-button-wrapper__large--on-blueberry"]]: isLarge && onBlueberry && isFocused
  });
  const radioButtonLabelClasses = classNames(
    radioButtonStyles["radio-button-label"],
    {
      [radioButtonStyles["radio-button-label--disabled"]]: disabled,
      [radioButtonStyles["radio-button-label--on-dark"]]: onDark,
      [radioButtonStyles["radio-button-label--invalid"]]: invalid,
      [radioButtonStyles["radio-button-label__large"]]: isLarge,
      [radioButtonStyles["radio-button-label__large--disabled"]]: isLarge && disabled
    },
    labelClassNames
  );
  const radioButtonClasses = classNames(
    radioButtonStyles["radio-button"],
    {
      [radioButtonStyles["radio-button--on-dark"]]: onDark,
      [radioButtonStyles["radio-button--disabled"]]: disabled,
      [radioButtonStyles["radio-button--on-blueberry"]]: onBlueberry,
      [radioButtonStyles["radio-button--invalid"]]: invalid,
      [radioButtonStyles["radio-button__large"]]: isLarge,
      [radioButtonStyles["radio-button__large--disabled"]]: isLarge && disabled,
      [radioButtonStyles["radio-button__large--invalid"]]: isLarge && invalid
    },
    className
  );
  const change = (e) => {
    changeChecked(e.target.checked);
    onChange && onChange(e);
  };
  const getLabelContent = () => /* @__PURE__ */ jsx(
    "input",
    {
      id: inputId,
      name,
      className: radioButtonClasses,
      type: "radio",
      disabled,
      value,
      ref: mergedRefs,
      defaultChecked,
      "aria-describedby": getAriaDescribedBy(props, errorTextUuid),
      required,
      ...rest,
      onChange: (e) => change(e)
    }
  );
  return /* @__PURE__ */ jsx(ErrorWrapper, { className: errorWrapperClassName, errorText, errorTextId: errorTextUuid, children: /* @__PURE__ */ jsx("div", { "data-testid": testId, "data-analyticsid": AnalyticsId.RadioButton, className: radioButtonWrapperClasses, children: renderLabelAsParent(
    label,
    getLabelContent(),
    inputId,
    onColor,
    radioButtonLabelClasses,
    void 0,
    radioButtonStyles["radiobutton-sublabel-wrapper"],
    isLarge
  ) }) });
});
RadioButton.displayName = "RadioButton";
export {
  RadioButton as R,
  getRadioLabelClasses as g
};
//# sourceMappingURL=RadioButton.js.map
